import { NavLink, useNavigate } from "react-router-dom";
import {
  House,
  ClipboardText,
  AddressBook,
  Package,
  Wrench,
  CurrencyDollar,
  Receipt,
  Buildings,
  SignOut,
} from "@phosphor-icons/react";
import { useCompany } from "@/lib/CompanyContext";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/ordens", label: "Ordens de Serviço", icon: ClipboardText, testid: "nav-orders" },
  { to: "/cadastros", label: "Cadastros", icon: AddressBook, testid: "nav-cadastros" },
  { to: "/estoque", label: "Estoque", icon: Package, testid: "nav-inventory" },
  { to: "/servicos", label: "Catálogo Serviços", icon: Wrench, testid: "nav-services" },
  { to: "/financeiro", label: "Financeiro", icon: CurrencyDollar, testid: "nav-financial" },
  { to: "/notas", label: "Notas Fiscais", icon: Receipt, testid: "nav-fiscal" },
  { to: "/empresa", label: "Empresa", icon: Buildings, testid: "nav-company" },
];

export default function Sidebar() {
  const { company } = useCompany();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const displayName = (company?.nome || "").trim() || "Oficina";
  const slogan = (company?.slogan || "").trim() || "Gestão Pro";
  const logo = company?.logo_base64;
  const initials = displayName.charAt(0).toUpperCase() || "O";

  const onLogout = async () => {
    await logout();
    toast.success("Sessão encerrada");
    navigate("/login", { replace: true });
  };

  return (
    <aside
      className="fixed left-0 top-0 h-screen w-[240px] bg-[#001E50] text-white flex flex-col z-40"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="px-6 pt-7 pb-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          {logo ? (
            <div className="w-11 h-11 rounded-md bg-white p-1 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={logo} alt={displayName} className="max-w-full max-h-full object-contain" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-md bg-[#00B0F0] flex items-center justify-center font-heading font-bold text-[#001E50] text-lg flex-shrink-0">
              {initials}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading text-lg leading-tight truncate" title={displayName}>{displayName}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 truncate" title={slogan}>
              {slogan}
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-3 border-transparent transition-all duration-200 ${
                  isActive
                    ? "side-active"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span>{it.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="px-6 py-4 border-t border-white/10">
        {user && (
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Conectado</div>
            <div className="text-xs text-white/80 truncate" title={user.email} data-testid="user-email">{user.email}</div>
          </div>
        )}
        <button
          type="button"
          onClick={onLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 text-xs text-white/60 hover:text-white transition-colors duration-150"
        >
          <SignOut size={14} weight="bold" /> Sair
        </button>
      </div>
    </aside>
  );
}
